export type RetainerPeriodStatus = {
	name: string | null;
	budgetType: string | null;
	locked: boolean | null;
	targetMinutes: number | null;
	registeredMinutes: number | null;
	entryCounts: boolean | null;
	moveRefused: boolean | null;
};

export type PanelModel =
	| {kind: 'none'}
	| {kind: 'locked' | 'moveRefused'; periodName: string}
	| {
			kind: 'numbers';
			periodName: string;
			/** The target as drawn and shown in the header: never below zero. */
			targetMinutes: number;
			registeredMinutes: number;
			/** From the real target, so a negative target reads as hours over it. */
			remainingMinutes: number;
			overMinutes: number;
			/** The part of this entry that lands over the target. */
			entryOverMinutes: number;
			/** Fractions of the bar: registered within target, entry within target, rest of target, registered over, entry over. */
			segments: number[];
			showDivider: boolean;
			/** Where the target ends on the bar, as a fraction of its width; 0 with no target or an empty bar. */
			targetFraction: number;
	  };

export const retainerPeriodPanelModel = (status: RetainerPeriodStatus | null, entryMinutes: number): PanelModel => {
	if (!status) return {kind: 'none'};
	const periodName = status.name ?? '';
	if (status.moveRefused) return {kind: 'moveRefused', periodName};
	if (status.locked) return {kind: 'locked', periodName};
	if (status.targetMinutes === null) return {kind: 'none'};

	const target = status.targetMinutes;
	const t = Math.max(target, 0);
	const r = status.registeredMinutes ?? 0;
	const e = status.entryCounts ? entryMinutes : 0;

	const registeredWithin = Math.min(r, t);
	const entryWithin = Math.min(e, Math.max(t - r, 0));
	const restOfTarget = Math.max(t - r - e, 0);
	const registeredOver = Math.max(r - t, 0);
	const entryOver = e - entryWithin;
	const total = Math.max(t, r + e);
	const minutes = [registeredWithin, entryWithin, restOfTarget, registeredOver, entryOver];

	const remaining = target - r - e;
	return {
		kind: 'numbers',
		periodName,
		targetMinutes: t,
		registeredMinutes: r,
		remainingMinutes: Math.max(remaining, 0),
		overMinutes: Math.max(-remaining, 0),
		entryOverMinutes: entryOver,
		segments: minutes.map(m => (total > 0 ? m / total : 0)),
		// With no target there is nothing to divide: the whole bar is over target.
		showDivider: t > 0 && registeredOver + entryOver > 0,
		targetFraction: total > 0 ? t / total : 0,
	};
};

/**
 * The entry's minutes as the panel counts them: the hours as typed, before the input commits them, unless the user
 * set billable time by hand (the modal otherwise keeps billable time in step with the hours); none on an entity that
 * is not billable.
 */
export const panelEntryMinutes = ({
	typedMinutes,
	billableEditedByHand,
	entityBillable,
	committedBillableMinutes,
}: {
	typedMinutes: number | null;
	billableEditedByHand: boolean;
	entityBillable: boolean;
	committedBillableMinutes: number | null | undefined;
}): number => {
	if (!entityBillable) return 0;
	if (typedMinutes != null && !billableEditedByHand) return typedMinutes;
	return committedBillableMinutes ?? 0;
};
